<template>
    <section :class="['tree-group', `tree-group--${group.kind}`]">
        <header class="tree-group__header">
            <span v-if="isFiltered" class="tree-group__toggle" aria-hidden="true"></span>
            <button
                v-else
                type="button"
                class="tree-group__toggle"
                :aria-expanded="!collapsed"
                :aria-label="collapsed ? 'Развернуть группу' : 'Свернуть группу'"
                @click="emit('toggle', group.key)"
            >
                <span :class="['tree-group__chevron', { 'is-collapsed': collapsed }]" aria-hidden="true">▾</span>
            </button>

            <UserRow
                v-if="group.owner"
                class="tree-group__owner"
                :user="group.owner"
                :actor-id="actorId"
                :partner-names="partnerNames"
                :advertiser-names="advertiserNames"
                :busy="busyUserId === group.owner.id"
                is-owner
                :is-context="!group.isOwnerMatched"
                @action="(action, user) => emit('action', action, user)"
            />
            <h3 v-else class="tree-group__title">{{ group.title }}</h3>

            <span class="tree-group__count">{{ countLabel }}</span>
        </header>

        <div v-if="!collapsed" class="tree-group__members">
            <UserRow
                v-for="member in group.members"
                :key="member.id"
                :user="member"
                :actor-id="actorId"
                :partner-names="partnerNames"
                :advertiser-names="advertiserNames"
                :busy="busyUserId === member.id"
                @action="(action, user) => emit('action', action, user)"
            />
            <p v-if="!group.members.length" class="tree-group__empty">{{ emptyText }}</p>
        </div>
    </section>
</template>

<script setup>
import { computed } from 'vue';
import UserRow from './UserRow.vue';
import { GROUP_KIND } from './buildUserTree';
import { PLURAL, pluralize } from '../../../utils/plural';

const props = defineProps({
    group: { type: Object, required: true },
    collapsed: { type: Boolean, default: false },
    isFiltered: { type: Boolean, default: false },
    actorId: { type: Number, required: true },
    partnerNames: { type: Map, required: true },
    advertiserNames: { type: Map, required: true },
    busyUserId: { type: Number, default: null },
});

const emit = defineEmits(['toggle', 'action']);

const countLabel = computed(() => {
    const count = props.group.members.length;
    return `${count} ${pluralize(count, props.group.kind === GROUP_KIND.ADMINS ? PLURAL.ADMIN : PLURAL.CLIENT)}`;
});

const emptyText = computed(() => (props.isFiltered ? 'Нет клиентов, подходящих под фильтр' : 'У агентства пока нет клиентов'));
</script>

<style scoped lang="scss">
@use '../../../../scss/pages/admin/admin-tokens' as *;

.tree-group {
    border: 1px solid $admin-border-color;
    border-radius: $radius-lg;
    background: $admin-bg-primary;
    overflow: visible;

    & + & { margin-top: $spacing-md; }
}

.tree-group__header {
    display: flex;
    align-items: center;
    gap: $spacing-sm;
    padding: $spacing-xs $spacing-md $spacing-xs $spacing-xs;
    background: $admin-bg-secondary;
    border-radius: $radius-lg $radius-lg 0 0;
}

.tree-group__toggle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 28px;
    height: 28px;
    border: none;
    border-radius: $radius-sm;
    background: none;
    color: $admin-text-muted;
    cursor: pointer;

    &:hover { background: $admin-bg-hover; color: $admin-text-primary; }
}

.tree-group__chevron {
    display: inline-block;
    transition: transform 0.15s ease;

    &.is-collapsed { transform: rotate(-90deg); }
}

.tree-group__owner {
    flex: 1;
    min-width: 0;
    padding-left: 0;
}

.tree-group__title {
    flex: 1;
    margin: 0;
    padding: $spacing-md 0;
    font-size: $font-size-md;
    font-weight: 600;
    color: $admin-text-primary;
}

.tree-group__count {
    flex-shrink: 0;
    font-size: $font-size-xs;
    color: $admin-text-muted;
    white-space: nowrap;
}

.tree-group__members {
    padding-left: $spacing-xl;

    @media (max-width: $breakpoint-md) {
        padding-left: $spacing-sm;
    }
}

.tree-group__empty {
    margin: 0;
    padding: $spacing-md $spacing-lg;
    border-top: 1px solid $admin-border-color;
    font-size: $font-size-sm;
    color: $admin-text-light;
}
</style>
